import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import type { IPdfJobRef } from 'src/queue/pdf-job-queues';

import type {
  CustomerWithPendingOrdersResponse,
  CustomersWithPendingOrdersResponse,
} from './endpoints/CustomersWithPendingOrdersEndpoint.types';
import type { InvoiceListItem, InvoiceStatusValue, InvoicesResponse } from './endpoints/InvoicesEndpoint.types';
import type { InvoiceDetail } from './endpoints/InvoiceEndpoint.types';
import type {
  PendingCustomerOrder,
  PendingCustomerOrdersResponse,
} from './endpoints/PendingCustomerOrdersEndpoint.types';
import type { CreateInvoiceBody, CreateInvoiceResponse } from './endpoints/CreateInvoiceEndpoint.types';
import type { UpdateInvoiceBody, UpdateInvoiceResponse } from './endpoints/UpdateInvoiceEndpoint.types';
import type { ApproveInvoiceResponse } from './endpoints/ApproveInvoiceEndpoint.types';
import type { MarkSalesInvoiceAsPaidResponse } from './endpoints/MarkSalesInvoiceAsPaidEndpoint.types';
import type { DeleteInvoiceResponse } from './endpoints/DeleteInvoiceEndpoint.types';
import type { RemoveOrdersFromInvoiceResponse } from './endpoints/RemoveOrdersFromInvoiceEndpoint.types';
import type { AddOrdersToInvoiceResponse } from './endpoints/AddOrdersToInvoiceEndpoint.types';
import type { ExportInvoicesResponse } from './endpoints/ExportInvoicesEndpoint.types';
import type { PeppolDelivery, SendPeppolResponse } from '../peppol/peppol.types';

// ---------------------------------------------------------------------------
// Customers with pending orders (already migrated).
// ---------------------------------------------------------------------------

export interface CustomersWithPendingOrdersFilters {
  query?: string;
  dateFrom?: string;
  dateTo?: string;
  invoiceFrequency?: 'TRANSPORT' | 'WEEKLY' | 'MONTHLY';
}

export async function getCustomersWithPendingOrders(
  filters?: CustomersWithPendingOrdersFilters,
): Promise<CustomersWithPendingOrdersResponse> {
  const params = new URLSearchParams();

  if (filters?.query) params.set('query', filters.query);
  if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.set('dateTo', filters.dateTo);
  if (filters?.invoiceFrequency) params.set('invoiceFrequency', filters.invoiceFrequency);

  const queryString = params.toString();
  const url = `/api/v1/sale/customers-with-pending-orders${queryString ? `?${queryString}` : ''}`;

  return fetchEndpointData<CustomersWithPendingOrdersResponse>(url);
}

export type { CustomerWithPendingOrdersResponse, CustomersWithPendingOrdersResponse };

export const INVOICES_SWR_KEY = 'invoices';
export const INVOICE_SWR_KEY = 'invoice';
export const PENDING_CUSTOMER_ORDERS_SWR_KEY = 'pending-customer-orders';
export const PENDING_CUSTOMER_ORDERS_COUNT_SWR_KEY = 'pending-customer-orders-count';

export interface InvoicesFilters {
  search?: string;
  customerId?: number;
  maxDueDate?: string;
  status?: InvoiceStatusValue[];
  noIncasso?: boolean;
}

export async function getInvoices(
  cursor?: string | null,
  take?: number,
  filters?: InvoicesFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<InvoicesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.customerId != null) params.set('customerId', filters.customerId.toString());
  if (filters?.maxDueDate) params.set('maxDueDate', filters.maxDueDate);
  if (filters?.status) {
    for (const status of filters.status) {
      params.append('status', status);
    }
  }
  if (filters?.noIncasso) params.set('noIncasso', 'true');
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/invoices?${queryString}` : '/api/v1/invoices';
  return fetchEndpointData<InvoicesResponse>(url);
}

export async function getInvoice(id: string): Promise<InvoiceDetail> {
  return fetchEndpointData<InvoiceDetail>(`/api/v1/invoices/${id}`);
}

export async function getPendingCustomerOrders(
  customerId: number,
  orderNumber?: string,
): Promise<PendingCustomerOrdersResponse> {
  const params = new URLSearchParams();
  params.set('customerId', customerId.toString());
  if (orderNumber) params.set('orderNumber', orderNumber);
  return fetchEndpointData<PendingCustomerOrdersResponse>(
    `/api/v1/invoices/pending-customer-orders?${params.toString()}`,
  );
}

export type { InvoiceDetail, InvoiceListItem, PendingCustomerOrder, PendingCustomerOrdersResponse };

// ---------------------------------------------------------------------------
// Mutations (async fetchers — callers wrap in try/catch + local isLoading).
// ---------------------------------------------------------------------------

// The GraphQL CreateInvoiceInputData carried `date` as a Date scalar; over REST
// the client sends the same YYYY-MM-DD string it produced before and the server
// coerces it to a Date.
export type CreateInvoiceInput = Omit<CreateInvoiceBody, 'date'> & { date: string };

export async function createInvoice(data: CreateInvoiceInput): Promise<CreateInvoiceResponse> {
  return fetchEndpointData<CreateInvoiceResponse>('/api/v1/invoices', {
    method: 'POST',
    body: data,
  });
}

export type UpdateInvoiceInput = Omit<UpdateInvoiceBody, 'date'> & { date?: string | null };

export async function updateInvoice(invoiceId: string, data: UpdateInvoiceInput): Promise<UpdateInvoiceResponse> {
  return fetchEndpointData<UpdateInvoiceResponse>(`/api/v1/invoices/${invoiceId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function sendInvoice(
  invoiceId: string,
  options?: { contactIds?: number[]; sendEmail?: boolean; sendPeppol?: boolean },
): Promise<IPdfJobRef> {
  return fetchEndpointData<IPdfJobRef>(`/api/v1/invoices/${invoiceId}/send`, {
    method: 'POST',
    body: options ?? {},
  });
}

/** POST /v1/invoices/:id/send-peppol — manual push to Billit/Peppol. */
export async function sendInvoiceViaPeppol(invoiceId: string): Promise<SendPeppolResponse> {
  return fetchEndpointData<SendPeppolResponse>(`/api/v1/invoices/${invoiceId}/send-peppol`, {
    method: 'POST',
  });
}

/** POST /v1/invoices/:id/refresh-peppol-status — fetch the delivery status from Billit now. */
export async function refreshInvoicePeppolStatus(invoiceId: string): Promise<PeppolDelivery> {
  return fetchEndpointData<PeppolDelivery>(`/api/v1/invoices/${invoiceId}/refresh-peppol-status`, {
    method: 'POST',
  });
}

export async function generateInvoice(invoiceId: string): Promise<IPdfJobRef> {
  // Returns the enqueued invoice-pdf job reference ({ queue, jobId }) that the
  // client polls via runPdfJobAndRefresh.
  return fetchEndpointData<IPdfJobRef>(`/api/v1/invoices/${invoiceId}/generate`, {
    method: 'POST',
  });
}

export async function approveInvoice(invoiceId: string, invoiceNumber?: number): Promise<ApproveInvoiceResponse> {
  return fetchEndpointData<ApproveInvoiceResponse>(`/api/v1/invoices/${invoiceId}/approve`, {
    method: 'POST',
    body: { invoiceNumber },
  });
}

export async function markSalesInvoiceAsPaid(
  invoiceId: string,
  paidAt: string,
): Promise<MarkSalesInvoiceAsPaidResponse> {
  return fetchEndpointData<MarkSalesInvoiceAsPaidResponse>(`/api/v1/invoices/${invoiceId}/mark-as-paid`, {
    method: 'POST',
    body: { paidAt },
  });
}

export async function deleteInvoice(invoiceId: string): Promise<DeleteInvoiceResponse> {
  return fetchEndpointData<DeleteInvoiceResponse>(`/api/v1/invoices/${invoiceId}`, {
    method: 'DELETE',
  });
}

export async function removeOrdersFromInvoice(
  invoiceId: string,
  orderIds: string[],
): Promise<RemoveOrdersFromInvoiceResponse> {
  return fetchEndpointData<RemoveOrdersFromInvoiceResponse>(`/api/v1/invoices/${invoiceId}/remove-orders`, {
    method: 'POST',
    body: { orderIds },
  });
}

export async function addOrdersToInvoice(invoiceId: string, orderIds: string[]): Promise<AddOrdersToInvoiceResponse> {
  return fetchEndpointData<AddOrdersToInvoiceResponse>(`/api/v1/invoices/${invoiceId}/add-orders`, {
    method: 'POST',
    body: { orderIds },
  });
}

export async function exportInvoices(body: {
  startId: string;
  endId: string;
  financialPeriod: number;
}): Promise<ExportInvoicesResponse> {
  return fetchEndpointData<ExportInvoicesResponse>('/api/v1/invoices/export', {
    method: 'POST',
    body,
  });
}

// ---------------------------------------------------------------------------
// SWR hooks.
// ---------------------------------------------------------------------------

export function useInvoice(id: string) {
  return useSWR([INVOICE_SWR_KEY, id], () => getInvoice(id));
}

export function useInvoices(filters?: InvoicesFilters) {
  return useSWR([INVOICES_SWR_KEY, filters], () => getInvoices(undefined, undefined, filters));
}

export function usePendingCustomerOrders(customerId: number, orderNumber?: string) {
  return useSWR([PENDING_CUSTOMER_ORDERS_SWR_KEY, customerId, orderNumber], () =>
    getPendingCustomerOrders(customerId, orderNumber),
  );
}

// SWR cache-key predicate to invalidate every invoice list / pending-orders query
// (and the pending-orders count) after a create / add / remove. Pass to
// useSWRConfig().mutate(...).
export function isInvoiceListKey(key: unknown): boolean {
  if (!Array.isArray(key)) return false;
  const root = key[0];
  return (
    root === INVOICES_SWR_KEY ||
    root === PENDING_CUSTOMER_ORDERS_SWR_KEY ||
    root === PENDING_CUSTOMER_ORDERS_COUNT_SWR_KEY
  );
}
